import { useState } from 'react';
import { supabase, q, explain } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { useAsync } from '../lib/useAsync';
import { useGroup } from './GroupLayout';
import { effectiveRating } from '../lib/balance';
import { DEFAULT_ATTRS } from '../lib/constants';
import { EmptyState, ErrorBanner, Spinner } from '../components/Shared';
import PlayerRow from '../components/PlayerRow';
import PlayerFormModal from '../components/PlayerFormModal';
import Icon from '../components/Icon';

export default function SquadScreen() {
  const { group, isAdmin } = useGroup();
  const { user } = useAuth();
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [actionError, setActionError] = useState('');

  const { data: members, loading, error, reload } = useAsync(async () => {
    const rows = await q(
      supabase
        .from('group_members')
        .select('id, group_id, profile_id, role, positions, attributes, form, rating_offset, display_name, profiles(display_name)')
        .eq('group_id', group.id),
      'Loading the squad'
    );

    const withNames = (rows || []).map((m) => ({
      ...m,
      // Guests have no profile row, so their name lives on group_members.
      // For real accounts we prefer the profile name but fall back to the
      // group-level one, which matters if RLS hides other people's profiles.
      name: (m.profile_id ? m.profiles?.display_name : null) || m.display_name || 'Unnamed',
    }));

    // Best position is a nice-to-have. If the view is missing we still render
    // the squad rather than blanking the whole screen.
    let bestByMember = {};
    if (withNames.length) {
      const { data: stats } = await supabase
        .from('player_position_stats')
        .select('member_id, position, games_at_position, goals_at_position, assists_at_position')
        .in('member_id', withNames.map((m) => m.id));
      (stats || []).forEach((row) => {
        if (!row.games_at_position) return;
        const output = (row.goals_at_position + row.assists_at_position) / row.games_at_position;
        const current = bestByMember[row.member_id];
        if (!current || output > current.output) bestByMember[row.member_id] = { position: row.position, output };
      });
    }

    return withNames
      .map((m) => ({ ...m, bestPosition: bestByMember[m.id]?.position || null }))
      .sort((a, b) => effectiveRating(b) - effectiveRating(a));
  }, [group.id]);

  // Admins only. A rating is a claim about someone relative to everyone else,
  // so letting each player set their own is the one input guaranteed to be
  // biased — and it degrades the balancing for the whole group, not just them.
  const canEdit = () => isAdmin;

  const setRole = async (m, role) => {
    const verb = role === 'admin' ? 'Make' : 'Remove';
    const msg =
      role === 'admin'
        ? `Make ${m.name} an admin? They'll be able to edit every player, save teams and log results.`
        : `Remove admin from ${m.name}?`;
    if (!window.confirm(msg)) return;
    setActionError('');
    try {
      await q(supabase.from('group_members').update({ role }).eq('id', m.id), `${verb} admin`);
      reload();
    } catch (err) {
      // The database refuses to demote the last admin, so a group can never
      // lock itself out. That error is worth showing verbatim.
      setActionError(explain(err));
    }
  };

  const startNewGuest = () =>
    setEditing({
      id: null,
      profile_id: null,
      display_name: '',
      positions: [],
      attributes: { ...DEFAULT_ATTRS },
      form: 0,
    });

  const save = async (member) => {
    setSaving(true);
    setModalError('');
    try {
      if (member.id) {
        const payload = { positions: member.positions, attributes: member.attributes };
        if (!member.profile_id) payload.display_name = member.display_name;
        await q(supabase.from('group_members').update(payload).eq('id', member.id), 'Saving the player');
      } else {
        await q(
          supabase.from('group_members').insert({
            group_id: group.id,
            profile_id: null,
            display_name: member.display_name,
            positions: member.positions,
            attributes: member.attributes,
            role: 'member',
          }),
          'Adding the player'
        );
      }
      setEditing(null);
      reload();
    } catch (err) {
      setModalError(explain(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the squad? Their past game records go with them.`)) return;
    setActionError('');
    try {
      await q(supabase.from('group_members').delete().eq('id', m.id), 'Removing the player');
      reload();
    } catch (err) {
      setActionError(explain(err));
    }
  };

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h1 className="page__title">SQUAD</h1>
          <p className="page__sub">
            {members?.length ?? 0} player{members?.length === 1 ? '' : 's'}
          </p>
        </div>
        {isAdmin && (
          <button type="button" className="btn btn--primary btn--sm" onClick={startNewGuest}>
            <Icon name="plus" size={15} />
            Add guest
          </button>
        )}
      </header>

      <ErrorBanner error={error || actionError} onRetry={error ? reload : undefined} />

      {!loading && !isAdmin && members?.length > 0 && (
        <p className="hint">
          Ratings are set by the group's admins, so the same yardstick is applied to everyone. Ask one
          of them if yours looks wrong.
        </p>
      )}

      {loading && <Spinner label="Loading the squad…" />}

      {!loading && !error && members?.length === 0 && (
        <EmptyState
          text={`No players yet. Share the code ${group.join_code} so your mates can join, or add guests who don't want an account.`}
        />
      )}

      <div className="stack stack--tight">
        {(members || []).map((m) => (
          <PlayerRow
            key={m.id}
            player={m}
            canEdit={canEdit(m)}
            onEdit={() => setEditing(m)}
            onDelete={isAdmin && m.profile_id !== user.id ? () => remove(m) : null}
            onToggleAdmin={
              isAdmin && m.profile_id
                ? () => setRole(m, m.role === 'admin' ? 'member' : 'admin')
                : null
            }
            profileTo={`/g/${group.id}/player/${m.id}`}
          />
        ))}
      </div>

      {editing && (
        <PlayerFormModal
          player={editing}
          allowNameEdit={!editing.profile_id}
          onSave={save}
          onCancel={() => {
            setEditing(null);
            setModalError('');
          }}
          saving={saving}
          error={modalError}
        />
      )}
    </div>
  );
}
